import { useCallback, useMemo } from 'react';

import Fuse from 'fuse.js/min-basic';
import { uniqBy } from 'lodash-es';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { LocationItem } from 'src/app/pages/search/shared/types';
import { removeDiacritics } from 'src/app/shared/utils/string';

type Result = ModelHookResult<
	undefined,
	{
		searchForCountryName: (queryLocation: string, selectedLocation: string) => readonly LocationItem[];
	}
>;

export function useSearchForCountryName(): Result {
	const { getCountries } = useI18n();

	const countries = useMemo(() => getCountries(), [getCountries]);
	const fuseCountriesSet = useMemo(() => new Fuse(countries.map(({ name }) => name)), [countries]);

	return {
		actions: {
			searchForCountryName: useCallback(
				(queryLocation: string, selectedLocation: string) => {
					const filteredCountries = countries.filter((country) =>
						removeDiacritics(country.name.toLowerCase()).includes(removeDiacritics(queryLocation.toLowerCase())),
					);

					const codeExactMatch =
						queryLocation.length === 2
							? countries.filter((country) => country.code.toLowerCase() === queryLocation.toLowerCase())
							: [];

					const fuzzyCountryMatch = fuseCountriesSet.search(queryLocation);
					const filteredFuzzyCountries = fuzzyCountryMatch
						.map(({ item: suggestedCountry }) => countries.find(({ name }) => name === suggestedCountry))
						.filter((country) => !!country);

					const uniqueResults = uniqBy([...codeExactMatch, ...filteredCountries, ...filteredFuzzyCountries], 'code');
					return uniqueResults.slice(0, 3).map((country) => ({
						value: country.code,
						label: country.name,
						count: 0,
						isRefined: selectedLocation === country.code,
						type: 'country' as const,
					}));
				},
				[countries, fuseCountriesSet],
			),
		},
	};
}
